/**
 * SuperAdmin Control Tower (P12)
 *
 * Visibility-first observation surface with strictly gated overrides.
 * Default mode: Observation (read-only). Override Mode requires typed
 * CONFIRM + reason ≥ 10 chars and auto-expires after 10 minutes.
 *
 * Hard rules (do NOT relax):
 *   • SuperAdmin role only.
 *   • All override actions flow through canonical writers — invariants intact.
 *   • Every override is audited and the touched entity is flagged in
 *     controlTowerOverrides for analysis exclusion.
 *   • No bulk actions, no automation changes, no contractValue exposure.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, ShieldCheck, AlertTriangle, Activity, Users, Briefcase, Send, BarChart3, UsersRound, Network } from "lucide-react";
import { EnforcementModeBanner } from "@/components/networkExec/EnforcementModeBanner";
import { NetworkExecTab } from "@/components/networkExec/NetworkExecTab";
import { useRole } from "@/data/RoleContext";
import { useAppData } from "@/data/AppDataContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useControlTowerSession } from "@/hooks/useControlTowerSession";
import { useControlTowerOverrides } from "@/hooks/useControlTowerOverrides";
import { OVERRIDE_RATE_LIMIT, type ControlTowerSession } from "@/data/controlTower/session";
import { getControlTowerAudit, getOverridesPerAdmin } from "@/data/controlTower/audit";
import { getControlTowerOverrides } from "@/data/controlTower/overrides";
import { getContractorTelemetry } from "@/data/contractor/contractorTelemetry";
import { useFrictionLog } from "@/data/FrictionLogContext";
import { getContractorOfferMetrics } from "@/data/contractor/offerMetrics";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";

const ADMIN_ID = "super_admin";
const COMPLETED_STAGES = new Set(["Completed", "QA Review"]);

function formatRemaining(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function ControlTower() {
  const { role } = useRole();
  const navigate = useNavigate();
  const { jobs, contractors, marketplaceOffers, systemEvents } = useAppData();
  const { entries: frictionEntries } = useFrictionLog();
  const session = useControlTowerSession(ADMIN_ID);
  const overrides = useControlTowerOverrides(ADMIN_ID);

  // must be before the role-guard early-return so hooks are always
  // called in the same order (react-hooks/rules-of-hooks)
  const contractorMap = useMemo(() => new Map(contractors.map((c) => [c.id, c])), [contractors]);

  const [activateOpen, setActivateOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [activateError, setActivateError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Hard role guard — RoleRouteGuard also blocks but defence-in-depth.
  useEffect(() => {
    if (role !== "super_admin") navigate("/", { replace: true });
  }, [role, navigate]);

  // ── Snapshot ──────────────────────────────────────────────
  const snapshot = useMemo(() => {
    const activeContractors = contractors.filter((c) => c.active).length;
    const activeJobs = jobs.filter((j) => !COMPLETED_STAGES.has(j.currentStage)).length;
    const pendingOffers = marketplaceOffers.filter((o) => o.status === "pending").length;
    const now = Date.now();
    const expiringOffers = marketplaceOffers.filter(
      (o) => o.status === "pending" && new Date(o.expiresAt).getTime() - now < 60 * 60_000,
    ).length;
    const unscheduledJobs = jobs.filter(
      (j) => !!j.assignedSubcontractorId && !j.scheduledInstallDate,
    ).length;
    return { activeContractors, activeJobs, pendingOffers, expiringOffers, unscheduledJobs };
  }, [jobs, contractors, marketplaceOffers]);

  // ── Alerts ────────────────────────────────────────────────
  const alerts = useMemo(() => {
    const out: { id: string; severity: "warn" | "critical"; label: string }[] = [];
    contractors.forEach((c) => {
      const m = getContractorOfferMetrics(c.id, marketplaceOffers, jobs, systemEvents);
      if (m.expiryRate !== null && m.expiryRate >= 0.5) {
        out.push({ id: `expiry-${c.id}`, severity: "warn", label: `${c.companyName}: high expiry rate (${Math.round(m.expiryRate * 100)}%)` });
      }
    });
    contractors
      .filter((c) => c.active && !jobs.some((j) => j.assignedSubcontractorId === c.id))
      .slice(0, 5)
      .forEach((c) => {
        out.push({ id: `idle-${c.id}`, severity: "warn", label: `${c.companyName}: no active assignments` });
      });
    const stuckUnscheduled = jobs.filter(
      (j) => !!j.assignedSubcontractorId && !j.scheduledInstallDate,
    );
    if (stuckUnscheduled.length >= 3) {
      out.push({
        id: "stuck-unscheduled",
        severity: "critical",
        label: `${stuckUnscheduled.length} assigned jobs are unscheduled`,
      });
    }
    return out;
  }, [contractors, marketplaceOffers, jobs, systemEvents]);

  // ── Activation handler ────────────────────────────────────
  const handleActivate = () => {
    setActivateError(null);
    const r = session.activate({ reason, confirmText });
    if (r.ok !== true) {
      const reasonKey = (r as { reason: string }).reason;
      setActivateError(
        reasonKey === "confirm_mismatch"
          ? "You must type CONFIRM exactly."
          : reasonKey === "reason_too_short"
          ? "Reason must be at least 10 characters."
          : "Cannot activate override mode.",
      );
      return;
    }
    setActivateOpen(false);
    setConfirmText("");
    setReason("");
  };

  // ── Override action wrapper that surfaces errors ──────────
  const runAction = (label: string, fn: () => { ok: boolean; reason?: string; detail?: string }) => {
    setActionError(null);
    const r = fn();
    if (!r.ok) {
      setActionError(`${label} blocked: ${r.reason}${r.detail ? ` (${r.detail})` : ""}`);
    }
  };

  const isActive = session.isActive;
  const audit = getControlTowerAudit();
  const overrideLog = getControlTowerOverrides();
  const telemetry = getContractorTelemetry();
  const reports = frictionEntries.filter((e) => e.moduleName === "Contractor Portal" || e.description?.toLowerCase().includes("report"));
  const adminOverrideActivity = useMemo(() => getOverridesPerAdmin(24), []);

  const jobsPagination = usePagination(jobs, 25);
  const offersPagination = usePagination(marketplaceOffers, 25);

  // ── Contractor & job tables ───────────────────────────────
  const contractorRows = useMemo(() =>
    contractors.map((c) => {
      const m = getContractorOfferMetrics(c.id, marketplaceOffers, jobs, systemEvents);
      const assigned = jobs.filter((j) => j.assignedSubcontractorId === c.id);
      const completed = assigned.filter((j) => COMPLETED_STAGES.has(j.currentStage)).length;
      return {
        id: c.id,
        name: c.companyName,
        status: c.active ? "active" : "inactive",
        tier: "—",
        accepted: m.counts.accepted,
        declined: m.counts.declined,
        expired: m.counts.expired,
        assigned: assigned.length,
        completed,
      };
    }),
  [contractors, marketplaceOffers, jobs, systemEvents]);

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" /> SuperAdmin Control Tower
          </h1>
          <p className="text-sm text-muted-foreground">
            Pilot oversight • {isActive ? "Override Mode" : "Observation Mode"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isActive ? (
            <>
              <span className="text-xs font-mono text-muted-foreground">
                {session.session?.actionCount ?? 0}/{OVERRIDE_RATE_LIMIT} actions • {formatRemaining(session.remainingMs)}
              </span>
              <Button variant="destructive" size="sm" onClick={() => session.cancel(true)}>
                Kill switch
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setActivateOpen(true)}>
              <ShieldAlert className="h-4 w-4 mr-1" /> Enable overrides
            </Button>
          )}
        </div>
      </div>

      {/* Override active banner */}
      {isActive && (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <span className="font-medium">Overrides active — pilot signal may be biased.</span>
          <span className="text-muted-foreground">
            Touched entities are flagged for analysis exclusion. Window expires in {formatRemaining(session.remainingMs)}.
          </span>
        </div>
      )}

      <EnforcementModeBanner />

      {actionError && (
        <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
          {actionError}
        </div>
      )}

      {/* Snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Card className="p-3"><div className="text-xs text-muted-foreground">Active contractors</div><div className="text-2xl font-bold">{snapshot.activeContractors}</div></Card>
        <Card className="p-3"><div className="text-xs text-muted-foreground">Active jobs</div><div className="text-2xl font-bold">{snapshot.activeJobs}</div></Card>
        <Card className="p-3"><div className="text-xs text-muted-foreground">Pending offers</div><div className="text-2xl font-bold">{snapshot.pendingOffers}</div></Card>
        <Card className="p-3"><div className="text-xs text-muted-foreground">Expiring (1h)</div><div className="text-2xl font-bold">{snapshot.expiringOffers}</div></Card>
        <Card className="p-3"><div className="text-xs text-muted-foreground">Unscheduled assigned</div><div className="text-2xl font-bold">{snapshot.unscheduledJobs}</div></Card>
      </div>

      <Tabs defaultValue="contractors">
        <TabsList>
          <TabsTrigger value="contractors"><Users className="h-3.5 w-3.5 mr-1" /> Contractors</TabsTrigger>
          <TabsTrigger value="jobs"><Briefcase className="h-3.5 w-3.5 mr-1" /> Jobs</TabsTrigger>
          <TabsTrigger value="offers"><Send className="h-3.5 w-3.5 mr-1" /> Offers</TabsTrigger>
          <TabsTrigger value="telemetry"><BarChart3 className="h-3.5 w-3.5 mr-1" /> Telemetry</TabsTrigger>
          <TabsTrigger value="crews"><UsersRound className="h-3.5 w-3.5 mr-1" /> Crews</TabsTrigger>
          <TabsTrigger value="alerts"><AlertTriangle className="h-3.5 w-3.5 mr-1" /> Alerts</TabsTrigger>
          <TabsTrigger value="network"><Network className="h-3.5 w-3.5 mr-1" /> Network</TabsTrigger>
          <TabsTrigger value="audit"><Activity className="h-3.5 w-3.5 mr-1" /> Audit</TabsTrigger>
        </TabsList>

        {/* Contractors */}
        <TabsContent value="contractors">
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase">
                <tr>
                  <th className="text-left p-2">Contractor</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Tier</th>
                  <th className="text-right p-2">Acc</th>
                  <th className="text-right p-2">Dec</th>
                  <th className="text-right p-2">Exp</th>
                  <th className="text-right p-2">Assigned</th>
                  <th className="text-right p-2">Done</th>
                </tr>
              </thead>
              <tbody>
                {contractorRows.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="p-2 font-medium">{r.name}</td>
                    <td className="p-2"><Badge variant="outline">{r.status}</Badge></td>
                    <td className="p-2 text-xs">{r.tier}</td>
                    <td className="p-2 text-right tabular-nums">{r.accepted}</td>
                    <td className="p-2 text-right tabular-nums">{r.declined}</td>
                    <td className="p-2 text-right tabular-nums">{r.expired}</td>
                    <td className="p-2 text-right tabular-nums">{r.assigned}</td>
                    <td className="p-2 text-right tabular-nums">{r.completed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* Jobs */}
        <TabsContent value="jobs">
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase">
                <tr>
                  <th className="text-left p-2">Job</th>
                  <th className="text-left p-2">Stage</th>
                  <th className="text-left p-2">Assigned</th>
                  <th className="text-left p-2">Schedule</th>
                  <th className="text-right p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobsPagination.paged.map((j) => {
                  const c = contractors.find((c) => c.id === j.assignedSubcontractorId);
                  return (
                    <tr key={j.id} className="border-t">
                        <td className="p-2 font-medium">{String(j.jobNumber ?? j.id.slice(0, 8))}</td>
                      <td className="p-2 text-xs">{j.currentStage}</td>
                      <td className="p-2 text-xs">{c?.companyName ?? <span className="text-muted-foreground">unassigned</span>}</td>
                      <td className="p-2 text-xs">{j.scheduledInstallDate || <span className="text-muted-foreground">unscheduled</span>}</td>
                      <td className="p-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!isActive || !j.scheduledInstallDate}
                          onClick={() => runAction("Clear schedule", () => overrides.clearScheduleDate(j.id))}
                        >
                          Clear date
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <PaginationBar {...jobsPagination} />
          </Card>
        </TabsContent>

        {/* Offers */}
        <TabsContent value="offers">
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase">
                <tr>
                  <th className="text-left p-2">Offer</th>
                  <th className="text-left p-2">Job</th>
                  <th className="text-left p-2">Contractor</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Expires</th>
                  <th className="text-right p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {offersPagination.paged.map((o) => {
                  const c = contractors.find((c) => c.id === o.contractorId);
                  return (
                    <tr key={o.id} className="border-t">
                      <td className="p-2 font-mono text-xs">{o.id}</td>
                      <td className="p-2 text-xs">{o.jobId}</td>
                      <td className="p-2 text-xs">{c?.companyName ?? o.contractorId}</td>
                      <td className="p-2"><Badge variant="outline">{o.status}</Badge></td>
                      <td className="p-2 text-xs">{new Date(o.expiresAt).toLocaleString()}</td>
                      <td className="p-2 text-right space-x-1">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!isActive || o.status !== "pending"}
                          onClick={() => runAction("Withdraw", () => overrides.withdrawOffer(o.id))}
                        >
                          Withdraw
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!isActive}
                          onClick={() => runAction("Resend", () => overrides.resendOffer(o.id))}
                        >
                          Resend
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <PaginationBar {...offersPagination} />
          </Card>
        </TabsContent>

        {/* Telemetry */}
        <TabsContent value="telemetry">
          <div className="grid md:grid-cols-2 gap-3">
            <Card className="p-3">
              <h3 className="text-sm font-semibold mb-2">Anchor / Tab events</h3>
              <div className="text-xs space-y-1 max-h-80 overflow-y-auto">
                {telemetry.length === 0 && <div className="text-muted-foreground">No events yet.</div>}
                {telemetry.slice(-50).reverse().map((e, i) => (
                  <div key={i} className="font-mono">
                    <span className="text-muted-foreground">{e.timestamp.slice(11, 19)}</span>{" "}
                    <span className="font-semibold">{e.eventType}</span>{" "}
                    <span>{contractorMap.get(e.contractorId)?.companyName ?? e.contractorId.slice(0, 8)}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-3">
              <h3 className="text-sm font-semibold mb-2">Report-a-problem</h3>
              <div className="text-xs space-y-1 max-h-80 overflow-y-auto">
                {reports.length === 0 && <div className="text-muted-foreground">No reports yet.</div>}
                {reports.slice(-20).reverse().map((r) => (
                  <div key={r.id} className="border-b py-1">
                    <div className="font-medium">{r.description}</div>
                    <div className="text-muted-foreground">{r.createdAt}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Crews (light) */}
        <TabsContent value="crews">
          <Card className="p-3 text-sm">
            <p className="text-muted-foreground text-xs mb-2">Crew assignments per active contractor.</p>
            <div className="space-y-1">
              {contractors.filter((c) => c.active).map((c) => {
                const myJobs = jobs.filter((j) => j.assignedSubcontractorId === c.id);
                const crews = new Set(myJobs.map((j) => j.crewId).filter(Boolean));
                return (
                  <div key={c.id} className="flex items-center justify-between border-b py-1.5 text-xs">
                    <span className="font-medium">{c.companyName}</span>
                    <span className="text-muted-foreground">{crews.size} crews • {myJobs.length} jobs</span>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        {/* Alerts */}
        <TabsContent value="alerts">
          <Card className="p-3">
            {alerts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No alerts.</p>
            ) : (
              <ul className="space-y-1.5">
                {alerts.map((a) => (
                  <li key={a.id} className="flex items-center gap-2 text-sm">
                    <Badge variant={a.severity === "critical" ? "destructive" : "outline"}>{a.severity}</Badge>
                    <span>{a.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>

        {/* Network — Phase 1c.2 */}
        <TabsContent value="network">
          <NetworkExecTab isSuperAdmin={role === "super_admin"} actorId={ADMIN_ID} actorRole={role} />
        </TabsContent>

        {/* Audit */}
        <TabsContent value="audit">
          <Card className="p-3 mb-3">
            <h3 className="text-sm font-semibold mb-2">Override activity (last 24h)</h3>
            {adminOverrideActivity.length === 0 ? (
              <p className="text-xs text-muted-foreground">No override actions in the last 24 hours.</p>
            ) : (
              <ul className="text-xs space-y-1">
                {adminOverrideActivity.map((a) => (
                  <li key={a.adminId} className="flex justify-between">
                    <span className="font-mono">{a.adminId}</span>
                    <span className="text-muted-foreground">{a.count} override{a.count === 1 ? "" : "s"}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 uppercase">
                <tr>
                  <th className="text-left p-2">When</th>
                  <th className="text-left p-2">Event</th>
                  <th className="text-left p-2">Actor</th>
                  <th className="text-left p-2">Target</th>
                  <th className="text-left p-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {audit.length === 0 && (
                  <tr><td className="p-3 text-muted-foreground" colSpan={5}>No audit events yet.</td></tr>
                )}
                {audit.slice().reverse().map((e) => (
                  <tr key={e.id} className="border-t">
                    <td className="p-2 font-mono">{e.timestamp.slice(11, 19)}</td>
                    <td className="p-2">{e.eventType}</td>
                    <td className="p-2">{e.adminId}</td>
                    <td className="p-2">{e.jobId ?? e.contractorId ?? e.offerId ?? "—"}</td>
                    <td className="p-2">{e.reason ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="p-2 text-xs text-muted-foreground border-t">
              Override-touched entities flagged: {overrideLog.length}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Activation modal */}
      <Dialog open={activateOpen} onOpenChange={setActivateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-destructive" /> Enable Override Mode
            </DialogTitle>
            <DialogDescription>
              Overrides bias the pilot signal. Touched entities will be flagged for analysis
              exclusion. Window: 10 minutes • Max {OVERRIDE_RATE_LIMIT} actions.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Reason (≥ 10 characters)</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why are overrides necessary right now?"
                rows={3}
              />
            </div>
            <div>
              <label className="text-xs font-medium">Type CONFIRM to enable</label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="CONFIRM"
              />
            </div>
            {activateError && (
              <div className="text-xs text-destructive">{activateError}</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivateOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleActivate}>Enable overrides</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
